/**
 * Storey -> element tree, built from what the v1 mesh surface actually carries.
 *
 * There is no model tree on the wire. What exists is:
 *
 *   - `ChunkHeader.storey_level` / `ManifestChunk.storey_name` — which storey a
 *     chunk belongs to;
 *   - the per-vertex `instanceIndex` (u32 at byte 8 of the interleaved vertex);
 *   - the whole-model `guid_table`, indexed by that same `instanceIndex`.
 *
 * So the tree here is exactly two levels deep and its leaves are **GUIDs, not
 * names**: `IfcRoot.Name`, the IFC class and the spatial containment hierarchy
 * are all outside the mesh surface. Presenting an element as anything other
 * than its identity would mean inventing data the engine did not produce, which
 * is why the panel says so in its caption and in each row's tooltip.
 *
 * Everything is derived in ONE pass per chunk, during streaming, over typed
 * arrays the renderer already holds — no second decode, no extra engine call.
 */

/** Rows listed per storey before the tree collapses the rest into a "+N" row. */
export const DEFAULT_MAX_ELEMENT_ROWS = 500;

/**
 * The slice of a loaded chunk the index needs.
 *
 * Structurally satisfied by `LoadedChunk`, and deliberately narrower: it holds
 * no three.js types, so the index can be built and tested from decoded chunk
 * bytes alone (the E2E harness does exactly that, with no scene and no GPU).
 */
export interface HierarchyChunkInput {
  readonly chunkId: number;
  readonly storeyLevel: number;
  readonly storeyName: string | null;
  /** Per-vertex `instanceIndex` at element `vertexIndex * 3 + 2`. */
  readonly instanceIndex: Uint32Array;
  readonly indexArray: Uint16Array | Uint32Array;
}

export interface HierarchyElement {
  readonly instanceIndex: number;
  /**
   * `guid_table[instanceIndex]`: a canonical UUID when the source `GlobalId`
   * was a valid compressed id, the raw source string otherwise, `null` when the
   * table is shorter than the index. Duplicates are possible and are preserved.
   */
  readonly identity: string | null;
  /** Triangles this element contributes **to this storey**. */
  readonly triangleCount: number;
  /** Every loaded chunk carrying a triangle of this element, in any storey. */
  readonly chunkIds: readonly number[];
}

export interface HierarchyStorey {
  readonly level: number;
  /** From the manifest; `null` when the model named no storey. */
  readonly name: string | null;
  readonly chunkIds: readonly number[];
  /** Sorted by triangle count descending, then `instanceIndex` ascending. */
  readonly elements: readonly HierarchyElement[];
  readonly triangleCount: number;
}

export interface ModelHierarchy {
  /** Sorted by `storey_level` ascending. */
  readonly storeys: readonly HierarchyStorey[];
  /** Distinct `(storey, instanceIndex)` leaves — an element in two storeys counts twice. */
  readonly elementCount: number;
  /**
   * `instanceIndex` -> every loaded chunk carrying one of its triangles. This is
   * what focusing an element walks; an element split across chunks (a storey
   * sub-split past `U16_VERTEX_LIMIT`) has more than one entry.
   */
  readonly chunksByInstance: ReadonlyMap<number, readonly number[]>;
}

interface StoreyAccumulator {
  readonly level: number;
  name: string | null;
  readonly chunkIds: number[];
  /** `instanceIndex` -> triangles contributed to this storey. */
  readonly triangles: Map<number, number>;
  totalTriangles: number;
}

/**
 * Accumulates the tree while chunks stream in.
 *
 * Cost is O(triangles) per chunk over already-decoded typed arrays, plus one
 * map entry per `(storey, instanceIndex)` pair and one per instance overall.
 * That is the honest price of a per-element tree: nothing on the wire indexes
 * triangles by product, so the consumer counts them or does without the tree.
 */
export class HierarchyIndex {
  private readonly storeys = new Map<number, StoreyAccumulator>();
  private readonly chunksByInstance = new Map<number, number[]>();

  add(chunk: HierarchyChunkInput): void {
    let storey = this.storeys.get(chunk.storeyLevel);
    if (storey === undefined) {
      storey = {
        level: chunk.storeyLevel,
        name: chunk.storeyName,
        chunkIds: [],
        triangles: new Map<number, number>(),
        totalTriangles: 0,
      };
      this.storeys.set(chunk.storeyLevel, storey);
    } else if (storey.name === null) {
      // Chunks of one storey are sub-splits of the same storey; the first
      // manifest entry that names it wins for all of them.
      storey.name = chunk.storeyName;
    }
    storey.chunkIds.push(chunk.chunkId);

    const indices = chunk.indexArray;
    const instances = chunk.instanceIndex;
    for (let offset = 0; offset + 2 < indices.length; offset += 3) {
      // Every vertex of a triangle belongs to the same product, so the first
      // corner decides — the same assumption `Viewer.pick` makes.
      const instance = instances[indices[offset] * 3 + 2];
      storey.triangles.set(instance, (storey.triangles.get(instance) ?? 0) + 1);
      storey.totalTriangles += 1;

      const chunkIds = this.chunksByInstance.get(instance);
      if (chunkIds === undefined) {
        this.chunksByInstance.set(instance, [chunk.chunkId]);
      } else if (chunkIds[chunkIds.length - 1] !== chunk.chunkId) {
        // Chunks arrive one at a time in `chunk_id` order, so a duplicate can
        // only ever be the previous push — no membership scan is needed.
        chunkIds.push(chunk.chunkId);
      }
    }
  }

  /** Freeze the accumulated counts into the tree the panel renders. */
  build(guidTable: readonly string[]): ModelHierarchy {
    const storeys: HierarchyStorey[] = [];
    let elementCount = 0;

    for (const accumulator of [...this.storeys.values()].sort((a, b) => a.level - b.level)) {
      const elements: HierarchyElement[] = [];
      for (const [instanceIndex, triangleCount] of accumulator.triangles) {
        elements.push({
          instanceIndex,
          identity: guidTable[instanceIndex] ?? null,
          triangleCount,
          chunkIds: this.chunksByInstance.get(instanceIndex) ?? [],
        });
      }
      // Biggest first: with a capped list, the elements a user is looking for
      // by eye are the ones that dominate the storey, and the ordering is
      // deterministic for a given model.
      elements.sort((a, b) =>
        b.triangleCount === a.triangleCount
          ? a.instanceIndex - b.instanceIndex
          : b.triangleCount - a.triangleCount,
      );
      elementCount += elements.length;
      storeys.push({
        level: accumulator.level,
        name: accumulator.name,
        chunkIds: [...accumulator.chunkIds],
        elements,
        triangleCount: accumulator.totalTriangles,
      });
    }

    return { storeys, elementCount, chunksByInstance: this.chunksByInstance };
  }
}

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

function storeyLabel(storey: HierarchyStorey): string {
  return storey.name === null ? `level ${storey.level}` : `${storey.name}（level ${storey.level}）`;
}

/** A client-supplied predicate that decides which rows are visible. */
export interface HierarchyFilter {
  /** Return true when the row for this element should be shown. */
  readonly matches: (element: HierarchyElement, label: string | null) => boolean;
}

export interface HierarchyPanelOptions {
  /** A tree row was activated. The shell selects and focuses it. */
  readonly onSelectElement: (element: HierarchyElement, storey: HierarchyStorey) => void;
  /** A storey's visibility was toggled. The shell hides that storey's chunks. */
  readonly onStoreyVisibilityChanged: (storey: HierarchyStorey, visible: boolean) => void;
  /** Rows listed per storey. Defaults to {@link DEFAULT_MAX_ELEMENT_ROWS}. */
  readonly maxElementRows?: number;
  /**
   * Optional: return a display label for one element, or `null` to fall back
   * to the GUID. Called at row-build time, not per frame — the tree is built
   * lazily on first expansion.
   */
  readonly elementLabel?: (element: HierarchyElement) => string | null;
  /** Optional filter. Matching storeys are expanded automatically. */
  readonly filter?: HierarchyFilter;
}

export interface HierarchyPanel {
  /**
   * Mark an element as the current selection, expanding its storey and
   * scrolling it into view. A no-op for an element beyond the listed cap —
   * the row does not exist, and building it would defeat the cap.
   */
  highlight(instanceIndex: number | null): void;
  /** Re-mark every storey as visible. Does NOT touch the scene; the caller already did. */
  showAllStoreys(): void;
  /** Re-mark every storey as hidden. Does NOT touch the scene; the caller already did. */
  hideAllStoreys(): void;
  /**
   * Show only the storey with the given `level` and hide all others.
   * Does NOT touch the scene; the caller already did.
   */
  isolateStorey(level: number): void;
}

/** A panel over a model with no storeys; every method is a no-op. */
const EMPTY_PANEL: HierarchyPanel = {
  highlight: () => undefined,
  showAllStoreys: () => undefined,
  hideAllStoreys: () => undefined,
  isolateStorey: () => undefined,
};

/**
 * Build the tree's DOM.
 *
 * Two things keep this cheap on a large model: storeys are collapsed on load
 * and a storey's rows are not built until it is first expanded, and each storey
 * lists at most `maxElementRows` of them. A model with 200k products therefore
 * costs one row per storey up front, and at most a few hundred rows per
 * expansion — never 200k nodes.
 */
export function renderHierarchyPanel(
  host: HTMLElement,
  model: ModelHierarchy,
  options: HierarchyPanelOptions,
): HierarchyPanel {
  host.replaceChildren();
  if (model.storeys.length === 0) {
    const empty = document.createElement("p");
    empty.className = "hint";
    empty.textContent = "階層はまだありません。/ No hierarchy yet.";
    host.append(empty);
    return EMPTY_PANEL;
  }

  const maxRows = options.maxElementRows ?? DEFAULT_MAX_ELEMENT_ROWS;
  const list = document.createElement("ul");
  list.className = "tree";

  /** `instanceIndex` -> the row that shows it, plus a way to reveal that row. */
  const rowIndex = new Map<number, { readonly row: HTMLElement; readonly expand: () => void }>();
  const storeyToggles: Array<{ readonly level: number; readonly setVisible: (visible: boolean) => void }> = [];
  let selected: HTMLElement | null = null;

  const filter = options.filter;
  const hasFilter = filter !== undefined;

  /** Cached label helper, used both for display and filtering. */
  const elementLabel = (element: HierarchyElement): string | null =>
    options.elementLabel?.(element) ?? null;

  for (const storey of model.storeys) {
    const visibleElements = hasFilter
      ? storey.elements.filter((element) => filter.matches(element, elementLabel(element)))
      : storey.elements;
    if (visibleElements.length === 0) {
      continue;
    }

    const item = document.createElement("li");
    item.className = "tree-storey";

    const header = document.createElement("div");
    header.className = "tree-storey-header";

    const children = document.createElement("ul");
    children.className = "tree-elements";
    // With an active filter, matched storeys start expanded so the user sees
    // the result immediately. Otherwise storeys stay collapsed on load.
    children.hidden = !hasFilter;

    let built = false;
    const buildRows = (): void => {
      if (built) {
        return;
      }
      built = true;
      const shown = Math.min(visibleElements.length, maxRows);
      for (let index = 0; index < shown; index += 1) {
        const element = visibleElements[index];
        const row = document.createElement("li");
        const button = document.createElement("button");
        button.type = "button";
        button.className = "tree-element";
        const label = elementLabel(element);
        const display = label ?? element.identity ?? `instance ${element.instanceIndex}`;
        button.title =
          label !== null
            ? `GUID: ${element.identity ?? "(none)"} — instance ${element.instanceIndex}`
            : `guid_table[${element.instanceIndex}] — ` +
              "メッシュ面は要素名を持たないため GUID を表示しています / " +
              "the mesh surface carries no element names, so the GUID is shown";
        const name = document.createElement("span");
        name.className = "tree-element-id";
        name.textContent = display;
        const count = document.createElement("span");
        count.className = "tree-element-count";
        count.textContent = `${formatCount(element.triangleCount)} tri`;
        button.append(name, count);
        button.addEventListener("click", () => {
          options.onSelectElement(element, storey);
        });
        row.append(button);
        children.append(row);
        if (!rowIndex.has(element.instanceIndex)) {
          rowIndex.set(element.instanceIndex, { row: button, expand });
        }
      }
      if (visibleElements.length > shown) {
        const more = document.createElement("li");
        more.className = "tree-more";
        if (hasFilter) {
          more.textContent =
            `+${formatCount(visibleElements.length - shown)} 件（フィルタ一致 ${formatCount(visibleElements.length)} 件 / ` +
            `全体 ${formatCount(storey.elements.length)} 件）` +
            ` / +${formatCount(visibleElements.length - shown)} more (` +
            `${formatCount(visibleElements.length)} matched / ${formatCount(storey.elements.length)} total)`;
        } else {
          more.textContent =
            `+${formatCount(visibleElements.length - shown)} 件（表示は ${formatCount(maxRows)} 件まで）` +
            ` / +${formatCount(visibleElements.length - shown)} more (list capped at ${formatCount(maxRows)})`;
        }
        children.append(more);
      }
    };

    const twisty = document.createElement("button");
    twisty.type = "button";
    twisty.className = "tree-twisty";
    twisty.textContent = hasFilter ? "▼" : "▶";
    twisty.setAttribute("aria-expanded", String(hasFilter));
    twisty.setAttribute("aria-label", `${storeyLabel(storey)} を展開 / expand`);

    const expand = (): void => {
      buildRows();
      children.hidden = false;
      twisty.textContent = "▼";
      twisty.setAttribute("aria-expanded", "true");
    };
    twisty.addEventListener("click", () => {
      if (children.hidden) {
        expand();
      } else {
        children.hidden = true;
        twisty.textContent = "▶";
        twisty.setAttribute("aria-expanded", "false");
      }
    });

    let visible = true;
    const label = document.createElement("button");
    label.type = "button";
    label.className = "tree-storey-label";
    label.setAttribute("aria-pressed", "true");
    label.title = "クリックでこの階の表示/非表示 / click to show or hide this storey";
    const labelText = document.createElement("span");
    labelText.className = "tree-storey-name";
    labelText.textContent = storeyLabel(storey);
    const labelCount = document.createElement("span");
    labelCount.className = "tree-storey-count";
    if (hasFilter) {
      labelCount.textContent =
        `${formatCount(visibleElements.length)} / ${formatCount(storey.elements.length)} elem / ` +
        `${formatCount(storey.triangleCount)} tri`;
    } else {
      labelCount.textContent =
        `${formatCount(storey.elements.length)} elem / ${formatCount(storey.triangleCount)} tri`;
    }
    label.append(labelText, labelCount);

    const applyVisibility = (next: boolean): void => {
      visible = next;
      label.setAttribute("aria-pressed", String(visible));
      label.classList.toggle("is-hidden", !visible);
    };
    label.addEventListener("click", () => {
      applyVisibility(!visible);
      options.onStoreyVisibilityChanged(storey, visible);
    });
    storeyToggles.push({ level: storey.level, setVisible: applyVisibility });

    header.append(twisty, label);
    item.append(header, children);
    list.append(item);

    if (hasFilter) {
      expand();
    }
  }

  if (list.children.length === 0) {
    const empty = document.createElement("p");
    empty.className = "hint";
    empty.textContent =
      "検索条件に一致する要素がありません。/ No elements match the filter.";
    host.append(empty);
    return EMPTY_PANEL;
  }

  host.append(list);

  return {
    highlight: (instanceIndex: number | null): void => {
      selected?.classList.remove("is-selected");
      selected = null;
      if (instanceIndex === null) {
        return;
      }
      const entry = rowIndex.get(instanceIndex);
      if (entry === undefined) {
        return;
      }
      entry.expand();
      entry.row.classList.add("is-selected");
      entry.row.scrollIntoView({ block: "nearest" });
      selected = entry.row;
    },
    showAllStoreys: (): void => {
      for (const toggle of storeyToggles) {
        toggle.setVisible(true);
      }
    },
    hideAllStoreys: (): void => {
      for (const toggle of storeyToggles) {
        toggle.setVisible(false);
      }
    },
    isolateStorey: (level: number): void => {
      for (const toggle of storeyToggles) {
        toggle.setVisible(toggle.level === level);
      }
    },
  };
}
